import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  FileText, 
  Shield, 
  Scale, 
  AlertCircle, 
  CheckCircle2,
  ArrowLeft,
  Building,
  Mail,
  Phone
} from "lucide-react";
import { Button } from "../components/ui/button";

const TermsAndConditions = () => {
  const lastUpdated = "7 de marzo de 2026";
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-navy-600 hover:text-navy-800 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Volver al inicio</span>
          </Link>
          <Link to="/">
            <img src="/logo.png" alt="Divídelo" className="h-8" onError={(e) => { e.target.style.display = 'none'; }} />
            <span className="font-heading text-xl font-bold text-navy-900">Divídelo</span>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-r from-navy-900 to-navy-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6"
          >
            <Scale className="w-5 h-5" />
            <span className="text-sm font-medium">Documento Legal</span>
          </motion.div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            Términos y Condiciones
          </h1>
          <p className="text-navy-200 text-lg">
            Última actualización: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-lg prose-navy max-w-none">
          
          {/* Intro */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-8"
          >
            <div className="flex items-start gap-4">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-emerald-800 mb-2">Resumen Importante</h3>
                <p className="text-emerald-700 text-sm">
                  Al utilizar los servicios de Divídelo, aceptas estos términos. Te recomendamos leerlos 
                  detenidamente. Divídelo es un servicio de <strong>asesoría y organización</strong> para 
                  procesos de visa, <strong>NO somos abogados ni prometemos aprobaciones</strong>.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Section 1 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-navy-100 rounded-lg flex items-center justify-center text-navy-600 text-sm font-bold">1</span>
              Definiciones
            </h2>
            <div className="pl-11 space-y-3 text-navy-700">
              <p><strong>"Divídelo"</strong>, <strong>"nosotros"</strong>, <strong>"nos"</strong> o <strong>"nuestro"</strong> se refiere a la plataforma de asesoría migratoria Divídelo y sus operadores.</p>
              <p><strong>"Usuario"</strong>, <strong>"usted"</strong> o <strong>"cliente"</strong> se refiere a cualquier persona que acceda o utilice nuestros servicios.</p>
              <p><strong>"Servicios"</strong> incluye la plataforma web, aplicación, asesoría, materiales educativos, y cualquier otro servicio proporcionado por Divídelo.</p>
              <p><strong>"Contenido"</strong> incluye texto, gráficos, imágenes, música, software, audio, video, información u otros materiales.</p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-navy-100 rounded-lg flex items-center justify-center text-navy-600 text-sm font-bold">2</span>
              Descripción del Servicio
            </h2>
            <div className="pl-11 space-y-4 text-navy-700">
              <p>Divídelo proporciona servicios de:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Asesoría migratoria:</strong> Orientación y guía en el proceso de solicitud de visa de turismo B1/B2 a Estados Unidos.</li>
                <li><strong>Organización documental:</strong> Sistema para gestionar y organizar los documentos requeridos.</li>
                <li><strong>Preparación para entrevista:</strong> Simuladores y materiales de práctica para la entrevista consular.</li>
                <li><strong>Seguimiento de proceso:</strong> Plataforma para monitorear el avance de tu solicitud.</li>
                <li><strong>Asistencia con formulario DS-160:</strong> Guía paso a paso para completar el formulario de solicitud.</li>
              </ul>
              
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-800 mb-1">Importante:</p>
                    <p className="text-amber-700 text-sm">
                      Divídelo <strong>NO</strong> es un bufete de abogados, <strong>NO</strong> proporciona asesoría legal, 
                      y <strong>NO</strong> puede garantizar la aprobación de ninguna visa. La decisión final sobre cualquier 
                      solicitud de visa recae exclusivamente en las autoridades consulares de Estados Unidos.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-navy-100 rounded-lg flex items-center justify-center text-navy-600 text-sm font-bold">3</span>
              Registro y Cuenta de Usuario
            </h2>
            <div className="pl-11 space-y-3 text-navy-700">
              <p>Para acceder a ciertos servicios, deberás crear una cuenta proporcionando información veraz, completa y actualizada.</p>
              <p><strong>Responsabilidades del usuario:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Mantener la confidencialidad de tu contraseña y datos de acceso.</li>
                <li>Notificarnos inmediatamente sobre cualquier uso no autorizado de tu cuenta.</li>
                <li>Proporcionar información veraz y precisa en todos los formularios.</li>
                <li>Actualizar tu información cuando sea necesario.</li>
              </ul>
              <p>Nos reservamos el derecho de suspender o cancelar cuentas que violen estos términos o proporcionen información falsa.</p>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-navy-100 rounded-lg flex items-center justify-center text-navy-600 text-sm font-bold">4</span>
              Planes y Pagos
            </h2>
            <div className="pl-11 space-y-3 text-navy-700">
              <p><strong>4.1 Precios:</strong> Los precios de nuestros planes están expresados en dólares estadounidenses (USD) y son pagos únicos, no recurrentes.</p>
              <p><strong>4.2 Métodos de pago:</strong> Aceptamos pagos mediante tarjeta de crédito/débito, transferencia bancaria y otros métodos especificados en la plataforma.</p>
              <p><strong>4.3 Política de reembolso:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Puedes solicitar un reembolso completo dentro de los primeros <strong>7 días</strong> desde tu compra si no has utilizado significativamente los servicios.</li>
                <li>Después de 7 días, o si has utilizado más del 30% de los servicios, no se otorgarán reembolsos.</li>
                <li>Los reembolsos se procesan en un plazo de 5-10 días hábiles al método de pago original.</li>
              </ul>
              <p><strong>4.4 Cambios de precio:</strong> Nos reservamos el derecho de modificar los precios. Los cambios no afectarán a compras ya realizadas.</p>
            </div>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-navy-100 rounded-lg flex items-center justify-center text-navy-600 text-sm font-bold">5</span>
              Uso Aceptable
            </h2>
            <div className="pl-11 space-y-3 text-navy-700">
              <p>Al usar nuestros servicios, te comprometes a:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Proporcionar información veraz y no falsificar documentos.</li>
                <li>No utilizar los servicios para actividades ilegales o fraudulentas.</li>
                <li>No compartir tu cuenta con terceros.</li>
                <li>No intentar acceder a sistemas o datos sin autorización.</li>
                <li>No reproducir, distribuir o vender el contenido de la plataforma.</li>
                <li>Respetar los derechos de propiedad intelectual.</li>
              </ul>
            </div>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-navy-100 rounded-lg flex items-center justify-center text-navy-600 text-sm font-bold">6</span>
              Limitación de Responsabilidad
            </h2>
            <div className="pl-11 space-y-3 text-navy-700">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                <p className="text-red-800 font-medium">
                  Divídelo no garantiza la aprobación de ninguna solicitud de visa. La decisión final 
                  corresponde exclusivamente a las autoridades consulares de Estados Unidos.
                </p>
              </div>
              <p><strong>6.1</strong> Nuestros servicios se proporcionan "tal cual" y "según disponibilidad".</p>
              <p><strong>6.2</strong> No somos responsables por:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Decisiones tomadas por autoridades migratorias o consulares.</li>
                <li>Pérdidas derivadas de información incorrecta proporcionada por el usuario.</li>
                <li>Interrupciones del servicio por causas fuera de nuestro control.</li>
                <li>Daños indirectos, incidentales o consecuentes.</li>
              </ul>
              <p><strong>6.3</strong> Nuestra responsabilidad máxima se limita al monto pagado por el usuario por los servicios.</p>
            </div>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-navy-100 rounded-lg flex items-center justify-center text-navy-600 text-sm font-bold">7</span>
              Propiedad Intelectual
            </h2>
            <div className="pl-11 space-y-3 text-navy-700">
              <p>Todo el contenido de la plataforma, incluyendo pero no limitado a textos, gráficos, logos, iconos, imágenes, clips de audio, descargas digitales y compilaciones de datos, es propiedad de Divídelo o sus proveedores de contenido.</p>
              <p>El uso de la plataforma no te otorga ningún derecho sobre nuestra propiedad intelectual más allá del uso personal y no comercial de los servicios contratados.</p>
            </div>
          </section>

          {/* Section 8 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-navy-100 rounded-lg flex items-center justify-center text-navy-600 text-sm font-bold">8</span>
              Modificaciones a los Términos
            </h2>
            <div className="pl-11 space-y-3 text-navy-700">
              <p>Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación en la plataforma.</p>
              <p>Te notificaremos sobre cambios significativos por correo electrónico o mediante un aviso destacado en la plataforma. El uso continuado de los servicios después de los cambios constituye tu aceptación de los nuevos términos.</p>
            </div>
          </section>

          {/* Section 9 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-navy-100 rounded-lg flex items-center justify-center text-navy-600 text-sm font-bold">9</span>
              Terminación
            </h2>
            <div className="pl-11 space-y-3 text-navy-700">
              <p>Podemos suspender o terminar tu acceso a los servicios si:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Violas estos términos y condiciones.</li>
                <li>Proporcionas información falsa o fraudulenta.</li>
                <li>Utilizas los servicios de manera que pueda dañar a Divídelo o a terceros.</li>
                <li>Por requerimiento legal o de autoridades competentes.</li>
              </ul>
            </div>
          </section>

          {/* Section 10 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-navy-100 rounded-lg flex items-center justify-center text-navy-600 text-sm font-bold">10</span>
              Ley Aplicable y Jurisdicción
            </h2>
            <div className="pl-11 space-y-3 text-navy-700">
              <p>Estos términos se rigen por las leyes aplicables. Cualquier disputa se resolverá mediante negociación amistosa y, de no ser posible, ante los tribunales competentes.</p>
            </div>
          </section>

          {/* Section 11 */}
          <section className="mb-10">
            <h2 className="flex items-center gap-3 text-2xl font-bold text-navy-900 mb-4">
              <span className="w-8 h-8 bg-navy-100 rounded-lg flex items-center justify-center text-navy-600 text-sm font-bold">11</span>
              Contacto
            </h2>
            <div className="pl-11 space-y-4 text-navy-700">
              <p>Para cualquier pregunta sobre estos términos, contáctanos:</p>
              <div className="bg-slate-50 rounded-xl p-6 space-y-3">
                <div className="flex items-center gap-3">
                  <Building className="w-5 h-5 text-navy-500" />
                  <span><strong>Divídelo</strong></span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-navy-500" />
                  <a href="mailto:info@dividelo.com" className="text-emerald-600 hover:underline">info@dividelo.com</a>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-navy-500" />
                  <a href="https://wa.me/19548059212" className="text-emerald-600 hover:underline">+1 (954) 805-9212</a>
                </div>
              </div>
            </div>
          </section>

        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/privacidad">
            <Button variant="outline" className="gap-2">
              <Shield className="w-4 h-4" />
              Ver Política de Privacidad
            </Button>
          </Link>
          <Link to="/">
            <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2">
              <ArrowLeft className="w-4 h-4" />
              Volver al inicio
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-navy-900 text-white py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-navy-300 text-sm">
            © {new Date().getFullYear()} Divídelo. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsAndConditions;
